import { useTranslation } from 'react-i18next';
import { Outlet, useMatches, useParams } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useSession } from '@/resources/auth/auth.service';
import { useOrganizations } from '@/resources/organization/organization.service';
import { PushPrompt } from '@/push/push-prompt';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from './app-sidebar';

function useRouteTitle(): string {
  const { t } = useTranslation();
  const matches = useMatches();
  const key = matches.filter((m) => m.staticData.titleKey).at(-1)?.staticData.titleKey;
  return key ? t(key) : '';
}

export function AppShell() {
  const { t } = useTranslation();
  const { user } = useSession();
  const organizations = useOrganizations();
  const title = useRouteTitle();
  const { organizationId } = useParams({ strict: false });

  if (!user) {
    return (
      <div className="node-grid flex min-h-svh items-center justify-center gap-2 text-sm text-muted-foreground">
        <LoaderCircle className="size-4 animate-spin" />
        {t('common.checkingConnection')}
      </div>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar
        user={user}
        organizations={organizations.data ?? []}
        activeOrganizationId={organizationId ?? null}
      />
      <SidebarInset>
        {}
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b bg-background/80 px-3 backdrop-blur md:px-6">
          <SidebarTrigger className="size-9 md:size-8" />
          <h2 className="font-heading text-sm font-medium">{title}</h2>
        </header>
        <div className="flex-1 p-4 md:p-6">
          <PushPrompt />
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
